// ./src/components/features/TemplateCategories/TemplateCategorySelector/TemplateCategorySelector.tsx
import React, { useMemo, useEffect, useRef } from "react";
import { Select } from "@mantine/core";
import { useTemplateCategories } from "@/hooks/useTemplateCategories";
import { LoadingIndicator } from "@/components/generic/LoadingIndicator";
import { TemplateCategory } from "@/utils/api/TemplateCategoryService";

export interface TemplateCategorySelectorProps {
  /** Selected template category ID */
  value: string | null;

  /** Callback when template category selection changes */
  onChange?: (categoryId: string | null, category?: TemplateCategory) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Custom filter function for template categories */
  categoryFilter?: (category: TemplateCategory) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether user is authenticated (required for the hook) */
  isAuthenticated?: boolean;

  /** Additional props passed to Select */
  [key: string]: any;
}

export const TemplateCategorySelector: React.FC<
  TemplateCategorySelectorProps
> = ({
  value,
  onChange,
  placeholder = "Select a category",
  categoryFilter,
  required = false,
  loadingPlaceholder = "Loading categories...",
  emptyText = "No categories available",
  error,
  disabled,
  size = "md",
  isAuthenticated = true,
  ...selectProps
}) => {
  const { templateCategories, loading, isEmpty, lastError } =
    useTemplateCategories({
      autoLoad: true,
      isAuthenticated,
    });

  const hasTriggeredInitialRef = useRef(false);

  // Filter and prepare template category options
  const categoryOptions = useMemo(() => {
    let filteredCategories = templateCategories;

    // Apply custom filter if provided
    if (categoryFilter) {
      filteredCategories = filteredCategories.filter(categoryFilter);
    }

    // Sort by name for better UX
    filteredCategories.sort((a, b) => {
      const nameA = a.name || "Unnamed Category";
      const nameB = b.name || "Unnamed Category";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredCategories.map((category) => ({
      value: category.id || "",
      label: category.name || "Unnamed Category",
    }));
  }, [templateCategories, categoryFilter]);

  // Create a map for quick category lookup by ID
  const categoryMap = useMemo(() => {
    const map = new Map();
    templateCategories.forEach((category) => {
      if (category.id) {
        map.set(category.id, category);
      }
    });
    return map;
  }, [templateCategories]);

  // Trigger initial onChange if value is already set when categories load
  useEffect(() => {
    if (
      !loading &&
      !hasTriggeredInitialRef.current &&
      value &&
      categoryMap.has(value) &&
      onChange
    ) {
      const selectedCategory = categoryMap.get(value);
      onChange(value, selectedCategory);
      hasTriggeredInitialRef.current = true;
    }
  }, [loading, value, categoryMap, onChange]);

  // Auto-select first category if required and no value is set
  useEffect(() => {
    if (
      required &&
      !value &&
      categoryOptions.length > 0 &&
      !loading &&
      onChange
    ) {
      const firstCategory = categoryOptions[0];
      if (firstCategory) {
        const selectedCategory = categoryMap.get(firstCategory.value);
        onChange(firstCategory.value, selectedCategory);
      }
    }
  }, [required, value, categoryOptions, loading, onChange, categoryMap]);

  // Handle onChange with category object lookup
  const handleChange = (selectedCategoryId: string | null) => {
    if (selectedCategoryId) {
      const selectedCategory = categoryMap.get(selectedCategoryId);
      onChange?.(selectedCategoryId, selectedCategory);
    } else {
      // When clearing selection, pass null for both ID and category
      onChange?.(null, undefined);
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && templateCategories.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading categories",
        disabled: true,
        error: error || "Failed to load categories",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: categoryOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && templateCategories.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Render the select component
  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={value ?? null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      rightSection={selectState.rightSection}
    />
  );
};
